import {
	BulkInvoiceCreateModal_company,
	BulkInvoiceCreateModal_project,
	BulkInvoiceCreateModal_retainerPeriod,
	InvoiceDTO,
} from './BulkInvoiceCreateModal';
import {addQuantityDecimals} from '../../../../../../../components/insights/invoicing/line-generation/invoicing_generate_lines_utils';
import {getUnitPrice} from '../../../../../../project-tab/projects/project-retainer-periods/RetainerPeriodUtil';
import {dateFragmentPropsFromIsoDateString} from '../../../../../util/DateUtil';
import Util from '../../../../../../shared/util/util';
import CreateInvoiceMutation from '../../../../../../../mutations/create_invoice_mutation';
import {invoiceLineConverter} from '../../../../../../../containers/modal/create_invoice_modal_v2/create_invoice_modal_v2';
import {notNullishAndPredicate} from '../../../../../util/NotNullPredicate';
import {addDays, format, formatISO} from 'date-fns';

export interface InvoicePeriodDTO {
	name: string | null | undefined;
	quantity: number;
	unitPrice: number;
	invoiceDate: string | null;
	dueDate: string | null;
	period: BulkInvoiceCreateModal_retainerPeriod;
	errorMessage?: string;
}

export interface CurrencyItem {
	id: string;
	currencyCode: string;
}

function getDueDate(period: BulkInvoiceCreateModal_retainerPeriod): string | null {
	if (!period?.endYear || !period.endMonth || !period.endDay) {
		return null;
	}
	return formatISO(addDays(new Date(period.endYear, period.endMonth - 1, period.endDay), 30), {representation: 'date'});
}

function getInvoiceDate(period: BulkInvoiceCreateModal_retainerPeriod): string | null {
	if (!period?.endYear || !period.endMonth || !period.endDay) {
		return null;
	}
	return formatISO(new Date(period.endYear, period.endMonth - 1, period.endDay), {representation: 'date'});
}

function getPeriodQuantity(period?: BulkInvoiceCreateModal_retainerPeriod): number {
	if (period?.periodBudgetType === 'FIXED_HOURS') {
		return period?.periodHoursAmount || 0;
	} else if (period?.periodBudgetType === 'FIXED_PRICE') {
		return 1;
	}
	return 0;
}

export function getCurrencyItems(company: BulkInvoiceCreateModal_company): CurrencyItem[] {
	if (!company?.exchangeRates?.edges) {
		return [];
	}
	const currencyItems: CurrencyItem[] = [];
	if (company.currency) {
		currencyItems.push({id: company.currency, currencyCode: company.currency});
	}
	company.exchangeRates.edges.forEach(edge => {
		const i = edge?.node;
		if (i && !!i.currency) {
			currencyItems.push({id: i.currency, currencyCode: i.currency});
		}
	});
	return currencyItems;
}

type getInvoicePeriodDataParams = {
	periods?: (BulkInvoiceCreateModal_retainerPeriod | undefined)[];
	currency: string;
	project: BulkInvoiceCreateModal_project;
	company: BulkInvoiceCreateModal_company;
};

export function getInvoicePeriodData({periods, currency, project, company}: getInvoicePeriodDataParams): InvoicePeriodDTO[] {
	if (!company || !project || !periods) {
		return [];
	}
	return (
		periods.filter(notNullishAndPredicate()).map(period => {
			return {
				period: period,
				name: period.name,
				quantity: getPeriodQuantity(period),
				unitPrice: getUnitPrice({
					period: period,
					invoiceCurrency: currency,
					rateCard: project.rateCard,
					company: company,
					timeRegs: project.timeRegistrations?.edges,
				}),
				invoiceDate: getInvoiceDate(period),
				dueDate: getDueDate(period),
			};
		}) || []
	);
}

function getInvoiceEntryDescription(period: BulkInvoiceCreateModal_retainerPeriod) {
	if (!period) {
		return '';
	}
	return `${period.name} (${format(Util.CreateDate(period.startYear, period.startMonth, period.startDay), 'PP')} - ${format(
		Util.CreateDate(period.endYear, period.endMonth, period.endDay),
		'PP'
	)})`;
}

type createInvoicesParams = {
	invoices?: InvoiceDTO[];
	project: BulkInvoiceCreateModal_project;
	invoiceQuantityDecimals?: number | null;
	onSuccess: () => void;
};

export async function createInvoices({
	invoices,
	project,
	invoiceQuantityDecimals,
	onSuccess,
}: createInvoicesParams): Promise<void> {
	if (!invoices) {
		return;
	}

	const commitInvoiceMutation = invoice => {
		return new Promise<void>(resolve => {
			Util.CommitMutation(CreateInvoiceMutation, invoice, resolve);
		});
	};

	for (let index = 0; index < invoices.length; index++) {
		const invoice = invoices[index];
		const invoiceDate = dateFragmentPropsFromIsoDateString(invoice.invoiceDate);
		const dueDate = dateFragmentPropsFromIsoDateString(invoice.dueDate);
		if (invoice && invoice.period) {
			const invoiceEntryDescription = getInvoiceEntryDescription(invoice.period);
			const newInvoiceData = {
				clientId: project?.client?.id,
				projectId: project?.id,
				invoiceType: null,
				name: invoice.name,
				currency: invoice.currency,
				invoiceReference: null,
				status: 'DRAFT',
				dueDay: dueDate.day,
				dueMonth: dueDate.month,
				dueYear: dueDate.year,
				createdDay: invoiceDate.day,
				createdMonth: invoiceDate.month,
				createdYear: invoiceDate.year,
				notes: '',
				entries: [
					invoiceLineConverter({
						description: invoiceEntryDescription,
						quantity: addQuantityDecimals(invoice.quantity, {invoiceQuantityDecimals}),
						unitPrice: invoice.unitPrice,
						projectId: project?.id,
						tax: invoice.tax,
						discount: 0,
						periodIds: [invoice.period.id],
					}),
				],
			};
			await commitInvoiceMutation(newInvoiceData);
		}
	}
	onSuccess();
}
